/**
 * ProgressIndicator - Packing progress display
 *
 * Shows verification progress with:
 * - "X of Y items to pack" text
 * - Percentage display
 * - Progress bar with green completion state
 * - Optional note about replaced items
 *
 * Provides clear visual feedback on packing progress.
 */
import { useTranslation } from 'react-i18next';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';

interface ProgressIndicatorProps {
  verified: number;
  total: number;
  failedItemCount?: number;
  className?: string;
}

export function ProgressIndicator({
  verified,
  total,
  failedItemCount = 0,
  className,
}: ProgressIndicatorProps) {
  const { t } = useTranslation('production');
  const percent = total > 0 ? Math.round((verified / total) * 100) : 0;
  const isComplete = verified === total && total > 0;

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex justify-between items-center">
        <span className="text-sm font-medium">
          {t('packer.progress.itemsToPack', { verified, total })}
          {failedItemCount > 0 && (
            <span className="text-xs text-muted-foreground ml-2">
              {t('packer.progress.replaced', { count: failedItemCount })}
            </span>
          )}
        </span>
        <span
          className={cn(
            'text-lg font-bold',
            isComplete ? 'text-green-600' : 'text-muted-foreground'
          )}
        >
          {percent}%
        </span>
      </div>
      <Progress
        value={percent}
        className={cn(
          'h-3',
          isComplete && 'bg-green-100 dark:bg-green-900 [&>div]:bg-green-600'
        )}
      />
    </div>
  );
}
